import React from 'react';
import { idGenerator } from '~/lib/helper/IdHelper';
import './SelectButtonList.scss';

export type ButtonSelectListProps = {
    className?: string;
    // Accessible name for the group, rendered as a visually-hidden <legend>.
    legend: string;
    children?: React.ReactNode;
};

// Distributes the per-instance radio group name from SelectButtonList to its
// items. Context (rather than cloning children) lets items sit behind wrapper
// elements, and the per-instance name keeps two lists on one page (e.g.
// OSSelector's distribution + version lists) from merging into one group. The
// default is undefined so a SelectButtonItem rendered with no enclosing list
// falls back to its own name rather than an empty one (which HTML treats as no
// group at all — silently breaking arrow-roving and the single tab stop).
const RadioGroupContext = React.createContext<string | undefined>(undefined);
const groupNameGen = idGenerator('selectButtonGroup');

export class SelectButtonList extends React.Component<ButtonSelectListProps> {
    private readonly groupName = groupNameGen();

    render() {
        return (
            <fieldset className={'selectButtonList ' + (this.props.className ?? '')}>
                <legend className="visually-hidden">{this.props.legend}</legend>
                <RadioGroupContext.Provider value={this.groupName}>{this.props.children}</RadioGroupContext.Provider>
            </fieldset>
        );
    }
}

export class MultiSelectButtonList extends React.Component<ButtonSelectListProps> {
    render() {
        return (
            <fieldset className={'selectButtonList ' + (this.props.className ?? '')}>
                <legend className="visually-hidden">{this.props.legend}</legend>
                {this.props.children}
            </fieldset>
        );
    }
}

export type MultiSelectButtonActionProps = {
    onSelect?: () => unknown;
    children?: React.ReactNode;
};

// An item inside `MultiSelectButtonList` that fires a one-shot action (e.g.
// "Add SSH Key" opens a modal) rather than toggling a selection. A native
// button, so screen readers announce it as an action — not as a "checkbox,
// not checked" like its MultiSelectButtonItem siblings.
export class MultiSelectButtonAction extends React.Component<MultiSelectButtonActionProps> {
    render() {
        return (
            <button type="button" className="btn btn-outline-secondary" onClick={() => this.props.onSelect?.()}>
                {this.props.children}
            </button>
        );
    }
}

export type SelectButtonItemProps = {
    isSelected: boolean;
    onSelect?: () => unknown;
    children?: React.ReactNode;
};

// The card is a <label> wrapping a visually-hidden native input: the input
// supplies the semantics and keyboard behaviour, the label supplies the whole
// card text as the input's accessible name, and the Bootstrap btn classes
// keep the existing card look. The input has no visible box of its own, so
// keyboard focus is shown on the card (`label:has(input:focus-visible)` in
// SelectButtonList.scss).
export class MultiSelectButtonItem extends React.Component<SelectButtonItemProps> {
    render() {
        const { isSelected, onSelect } = this.props;
        return (
            <label className={`btn ${isSelected ? 'btn-secondary' : 'btn-outline-secondary'}`}>
                <input type="checkbox" className="visually-hidden" checked={isSelected} onChange={() => onSelect?.()} />
                {this.props.children}
            </label>
        );
    }
}

export class SelectButtonItem extends React.Component<SelectButtonItemProps> {
    static contextType = RadioGroupContext;
    declare context: React.ContextType<typeof RadioGroupContext>;
    // Used only when there is no enclosing SelectButtonList to provide the group
    // name; keeps a stray item a self-consistent single-radio group.
    private readonly fallbackGroupName = groupNameGen();

    render() {
        const { isSelected, onSelect } = this.props;
        return (
            <label className={`btn ${isSelected ? 'btn-secondary' : 'btn-outline-secondary'}`}>
                <input
                    type="radio"
                    className="visually-hidden"
                    name={this.context ?? this.fallbackGroupName}
                    checked={isSelected}
                    onChange={() => onSelect?.()}
                />
                {this.props.children}
            </label>
        );
    }
}
